/**
 * modelRunInfo.ts
 * معلومات دورة النموذج المعروضة للمستخدم: أي دورة تُعرض الآن، متى وُلِّدت، ومتى يُتوقَّع
 * التحديث القادم — على غرار لوحة "نموذج التنبؤ" في Windy.
 *
 * المصدر هو meta.json الذي يكتبه refresh_all.py لكل مجلّد نموذج:
 *   { run_epoch, generated_epoch, hours, layers }
 *   - run_epoch       : زمن الدورة الاسمي (مثل 06Z) — ما تنسب إليه البيانات فعلاً.
 *   - generated_epoch : لحظة انتهاء التوليد على الخادم.
 *
 * تقدير التحديث القادم **يُشتقّ من البيانات نفسها** لا من ثوابت مكتوبة يدوياً:
 * تأخّر النشر المرصود = generated_epoch − run_epoch، فالتحديث القادم ≈ الدورة التالية
 * + هذا التأخّر. هكذا يصحّح التقدير نفسه تلقائياً إن تغيّر جدول الخادم أو زمن نشر
 * المزوّد، بدل أن يبقى رقماً ثابتاً يشيخ بصمت. ولهذا يُعرض موسوماً بأنه تقديري.
 */

import { useEffect, useState } from 'react';
import type { WeatherModelId } from '../store/types';

/**
 * كل النماذج المصدرية تُصدر أربع دورات يومياً (00/06/12/18Z)، لكن **تكرار سحبنا لها
 * مختلف** حسب crontab الخادم: GFS/ICON كل 6 ساعات (تطابق كل الدورات)، بينما ECMWF كل
 * 12 ساعة فقط (`0 1,13 * * *`) — لأن نشر بيانات ECMWF المجانية (open-data) يتأخّر فعلياً
 * ~7-9 ساعات عن وقتها الاسمي، فتشغيل الكرون كل 6 ساعات كان يعني غالباً عدم توفّر أي
 * دورة جديدة أصلاً عند نصف التشغيلات — حمل شبكة/خادم زائد بلا أي بيانات أحدث فعلياً،
 * لأن العنق الحقيقي عند نشر ECMWF نفسها لا عند جدولتنا.
 *
 * لذلك "متى نتوقّع تحديثاً جديداً" يجب أن يطابق **تكرار سحبنا الفعلي** لا تكرار الدورات
 * المصدرية النظري، وإلا ظهرت ECMWF دائماً "متأخّرة/متوقَّعة الآن" رغم أن كل شيء طبيعي
 * (رُصد فعلياً: بيانات عمرها 17 ساعة تُعرض متجاوزة موعدها بسبب هذا الافتراض الخاطئ).
 */
const CYCLE_INTERVAL_HOURS_BY_MODEL: Record<WeatherModelId, number> = {
    GFS: 6,
    ECMWF: 12,
    ICON: 6,
};

function cycleIntervalHours(model: WeatherModelId): number {
    return CYCLE_INTERVAL_HOURS_BY_MODEL[model] ?? 6;
}

/** حدود منطقية لتأخّر النشر المرصود: تشغيلة يدوية متأخّرة أو فاشلة قد تُخرج قيمة شاذّة. */
const MIN_LAG_HOURS = 2;
const MAX_LAG_HOURS = 14;

export interface ModelRunInfo {
    /** زمن الدورة الاسمي (ثوانٍ). */
    runEpoch: number;
    /** لحظة انتهاء التوليد على الخادم (ثوانٍ)، أو null في الدورات القديمة بلا الحقل. */
    generatedEpoch: number | null;
    /** عدد إطارات الشريط الزمني. */
    hours: number;
    /** ساعة الدورة بتوقيت UTC (0/6/12/18) — تُعرض كـ "06Z". */
    cycleHourUtc: number;
    /** الزمن المقدَّر للتحديث القادم (ثوانٍ)، أو null إن تعذّر التقدير. */
    nextExpectedEpoch: number | null;
}

export type ModelRunStatus = 'loading' | 'ready' | 'missing';

/**
 * مجلّد نُسج النموذج. **المصدر الوحيد** لهذا الربط في التطبيق كلّه.
 *
 * كانت هذه الدالة مكرَّرة حرفياً في ثلاثة ملفّات زائد سطرٍ رابع داخل NewWeatherMap،
 * فنقل GFS إلى مجلّده كان يقتضي إصابة أربعة مواضع بلا أن يُخطئ أحدها — ونسيانُ واحدٍ
 * منها يعني نموذجاً يقرأ نُسج نموذج آخر بصمت. فوُحّدت هنا وتستوردها البقيّة.
 *
 * ‏GFS كان يسكن `rasters/` المسطّح مشاركاً البروقَ والرادار والقمر الصناعي والمحيطات،
 * ولذلك تعذّر عليه ما حصل عليه ECMWF/ICON: النشر بقلب رابط خانة (راجع staging_begin
 * في gfs_to_raster.py). فعُزل في `rasters/gfs/` ليصير مثلهما — والمجلّد المسطّح يبقى
 * لما ليس نموذجاً.
 */
export function rasterDirFor(model: WeatherModelId): string {
    return model === 'ECMWF' ? 'rasters/ecmwf/'
        : model === 'ICON' ? 'rasters/icon/'
            : 'rasters/gfs/';
}

/** دلو ترقيم رابط meta.json — دقيقة واحدة (راجع metaUrl). */
const META_BUCKET_MS = 60_000;

/**
 * رابط meta.json مرقَّماً بدلو دقيقة واحدة.
 *
 * لماذا الترقيم أصلاً: `cache: 'no-store'` يتخطّى كاش المتصفّح فقط، ولا شأن له بكاش
 * الحافة. الموقع خلف Cloudflare (تؤكّده ترويسة `cf-cache-status: HIT` على هذا المسار
 * بالذات)، وقد رُصد أن الاستجابة الحيّة تحمل `max-age=7200` رغم أن vhost المستودع
 * يعطي meta.json ‏120 ثانية — أي أن جهة ما فوق nginx تُطيل المدّة. النتيجة المقيسة:
 * دورة جديدة على القرص منذ ساعة ونصف والواجهة ما زالت تعرض دورة قبل يومين، لأن الحافة
 * تخدم نسخة قديمة لكل الزوّار مهما فعل العميل.
 *
 * الدلو الدقيقي يجعل كل دقيقة رابطاً جديداً لا يملك أحد نسخة منه، فتصل الدورة الجديدة
 * خلال دقيقة بدل ساعتين، مع بقاء الطلبات المتكرّرة داخل الدقيقة الواحدة كاش-هت (تبديل
 * النماذج ذهاباً وإياباً لا يُحدث رحلة شبكة لكل نقرة). الملف بضع مئات من البايتات،
 * فكلفة تحديثه كل دقيقة لا تُذكر أمام عرض بيانات طقس متقادمة بصمت.
 */
export function metaUrl(dir: string): string {
    return `${import.meta.env.BASE_URL}${dir}meta.json?t=${Math.floor(Date.now() / META_BUCKET_MS)}`;
}

/**
 * آخر meta معروف لكل مجلّد نموذج — ذاكرة الجلسة فقط.
 *
 * سببه: الدلو الدقيقي أعلاه يجعل رابط meta جديداً كل دقيقة، فكل تبديل نموذج بعد انقضاء
 * الدقيقة يفرض **رحلة شبكة كاملة**. و`timelineReady` في الواجهة يحجب كل الطبقات حتى
 * تصل تلك الاستجابة — فيرى المستخدم خريطة عارية طوال زمن الرحلة عند كل نقرة تبديل.
 *
 * الحل نمط stale-while-revalidate: نخدم المحفوظ فوراً فتظهر الطبقات بلا انتظار، ونجلب
 * في الخلفية ونصحّح إن تغيّر. والتصحيح شبه معدوم عملياً: `run_epoch` يتغيّر مرّات معدودة
 * في اليوم، بينما التبديل بين النماذج يقع خلال ثوانٍ من جلبٍ سابق.
 *
 * **ذاكرة الجلسة لا التخزين الدائم**: عند فتح جديد للصفحة يُرجَّح أن تكون دورة جديدة قد
 * نُشرت، فالبدء بقيمة محفوظة من الأمس يعني ربط الشريط بزمن خاطئ لحظةً — وهي المخاطرة
 * الوحيدة التي لا نقبلها هنا.
 */
const metaCache = new Map<string, unknown>();

export function getCachedMeta(dir: string): unknown | null {
    return metaCache.get(dir) ?? null;
}

export function setCachedMeta(dir: string, meta: unknown): void {
    metaCache.set(dir, meta);
}

/** الطلبات الجارية بحسب الرابط المرقَّم — مفتاح إلغاء التكرار أدناه. */
const metaInFlight = new Map<string, Promise<unknown | null>>();

/**
 * جلبٌ **واحد** لـmeta.json مهما تعدّد الطالبون.
 *
 * ── لماذا ─────────────────────────────────────────────────────────────────────
 * خمسة مواضع في الواجهة تقرأ نفس الملفّ: محور الشريط، وطبقات النموذج المتاحة،
 * ومعلومات الدورة، وتجهيز النماذج، وإعادة فحص الدورة. كلٌّ منها كان يجلب بنفسه.
 *
 * وقياس الموقع الحيّ (2026-09-02) أظهر الثمن كاملاً: **ثلاثة طلبات متطابقة**
 * لـ`rasters/meta.json?t=29805634` انطلقت خلال 12 مللي ثانية من بعضها، وكلٌّ منها
 * استغرق **819 مللي ثانية** — لأن الرابط المرقَّم بالدقيقة جديدٌ دائماً فلا تملك
 * الحافة نسخةً منه، فتذهب كل رحلة إلى الأصل. أي ملفّ من كيلوبايت واحد كلّف ثلاث
 * رحلات كاملة إلى الخادم **قبل** أن يُطلب أوّل راستر. والراستر نفسه — 339 كيلوبايت —
 * وصل في **4 مللي ثانية** من الحافة.
 *
 * فالصورة لم تكن تتأخّر لحجمها، بل انتظاراً لبيانات وصفية طُلبت ثلاث مرّات بلا داعٍ.
 *
 * الوعد يُحفظ بمفتاح الرابط المرقَّم — فينقضي وحده مع انقضاء دلو الدقيقة، ولا يحتاج
 * إبطالاً يدوياً: دقيقة جديدة تعني رابطاً جديداً تعني جلباً جديداً.
 */
export function fetchRasterMeta(dir: string): Promise<unknown | null> {
    const url = metaUrl(dir);
    const existing = metaInFlight.get(url);
    if (existing) return existing;

    const task = fetch(url, { cache: 'no-store' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
    metaInFlight.set(url, task);
    // تنظيف بعد انقضاء الدلو: بلا هذا تتراكم روابط كل دقيقة في الخريطة طوال الجلسة.
    window.setTimeout(() => {
        if (metaInFlight.get(url) === task) metaInFlight.delete(url);
    }, META_BUCKET_MS + 1000);
    return task;
}

function buildInfo(meta: { run_epoch?: number; generated_epoch?: number; hours?: number }, model: WeatherModelId): ModelRunInfo | null {
    const runEpoch = Number(meta?.run_epoch);
    if (!Number.isFinite(runEpoch) || runEpoch <= 0) return null;

    const gen = Number(meta?.generated_epoch);
    const generatedEpoch = Number.isFinite(gen) && gen > 0 ? gen : null;

    let nextExpectedEpoch: number | null = null;
    if (generatedEpoch !== null) {
        const lagH = (generatedEpoch - runEpoch) / 3600;
        const lag = Math.min(MAX_LAG_HOURS, Math.max(MIN_LAG_HOURS, lagH));
        nextExpectedEpoch = runEpoch + (cycleIntervalHours(model) + lag) * 3600;
    }

    return {
        runEpoch,
        generatedEpoch,
        hours: Number(meta?.hours) || 0,
        cycleHourUtc: new Date(runEpoch * 1000).getUTCHours(),
        nextExpectedEpoch,
    };
}

/** يقرأ meta.json لمجلّد النموذج المختار ويعيد معلومات دورته. */
export function useModelRunInfo(model: WeatherModelId): { status: ModelRunStatus; info: ModelRunInfo | null } {
    const [state, setState] = useState<{ status: ModelRunStatus; info: ModelRunInfo | null }>(
        { status: 'loading', info: null });

    useEffect(() => {
        let cancelled = false;
        setState({ status: 'loading', info: null });
        // no-store لكاش المتصفّح + رابط مرقَّم بالدقيقة لكاش الحافة (راجع metaUrl):
        // هذه اللوحة تحديداً تُسأل لمعرفة "هل وصلت دورة جديدة؟"، فأي كاش على الطريق
        // يجعلها تكذب على المستخدم في اللحظة التي يفتحها فيها.
        fetchRasterMeta(rasterDirFor(model))
            .then((meta) => {
                if (cancelled) return;
                if (!meta) throw new Error('no meta');
                const info = buildInfo(meta as Parameters<typeof buildInfo>[0], model);
                setState(info ? { status: 'ready', info } : { status: 'missing', info: null });
            })
            .catch(() => { if (!cancelled) setState({ status: 'missing', info: null }); });
        return () => { cancelled = true; };
    }, [model]);

    return state;
}

/** مؤقّت يعيد الزمن الحالي بالمللي ثانية، يتحدّث كل ثانية (للعدّ التنازلي الحيّ). */
export function useNowTick(enabled = true): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!enabled) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [enabled]);
    return now;
}

/**
 * يصيغ مدّة بالمللي ثانية: "٣ س ١٢ د ٤٠ ث" تحت اليوم، و"٢ ي ٨ س" فوق يومين.
 * الثواني تظهر فقط تحت الساعة (وإلّا ارتجف الرقم بلا فائدة)، والأيام تظهر فوق 48 ساعة
 * (قراءة "56 س" لعمر بيانات متعطّلة أصعب بكثير من "2 ي 8 س").
 */
export function formatDuration(ms: number, lang: string): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const u = lang === 'ar'
        ? { d: 'ي', h: 'س', m: 'د', s: 'ث' }
        : { d: 'd', h: 'h', m: 'm', s: 's' };

    if (h >= 48) {
        const d = Math.floor(h / 24);
        return `${d} ${u.d} ${h % 24} ${u.h}`;
    }

    const parts: string[] = [];
    if (h > 0) parts.push(`${h} ${u.h}`);
    if (h > 0 || m > 0) parts.push(`${m} ${u.m}`);
    if (h === 0) parts.push(`${s} ${u.s}`);
    return parts.join(' ');
}

/**
 * هل البيانات متقادمة؟ أسوأ حالة طبيعية = تكرار سحبنا الفعلي للنموذج (راجع
 * CYCLE_INTERVAL_HOURS_BY_MODEL) + زمن نشرها (~11 ساعة لأبطأ نموذج). ما تجاوز ثلاثة
 * أضعاف هذا التكرار يعني أن التوليد متوقّف لا متأخّراً — وهو ما حدث فعلاً حين تجمّد
 * تنزيل معلّق يومين كاملين بلا أن ينتبه أحد. عتبة موحّدة (18 ساعة) كانت تُطلق إنذاراً
 * كاذباً على ECMWF تحديداً (تكرارها الفعلي 12 ساعة لا 6) في أسوأ حالة طبيعية لها.
 */
export function isRunStale(runEpoch: number, nowMs: number, model: WeatherModelId = 'GFS'): boolean {
    return nowMs - runEpoch * 1000 > 3 * cycleIntervalHours(model) * 3600 * 1000;
}
